import { pickMediaUrl } from "./formTabs";

export const formatVideoDuration = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return "00:00";

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);

  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

export const formatTotalTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) return "00:00:00";

  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

export const getVideoDurationSeconds = (response, videoRefs) => {
  const videoEl = videoRefs.current?.[response._id];
  if (videoEl && Number.isFinite(videoEl.duration) && videoEl.duration > 0) {
    return Promise.resolve(Math.floor(videoEl.duration));
  }

  const videoSrc = withVideoCacheBuster(
    getVideoUrlFromResponse(response),
    response._id,
  );
  if (!videoSrc) return Promise.resolve(0);

  return new Promise((resolve) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.src = videoSrc;

    const finish = (seconds) => {
      window.clearTimeout(timeoutId);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.src = "";
      resolve(seconds);
    };

    video.onloadedmetadata = () => {
      finish(
        Number.isFinite(video.duration) && video.duration > 0
          ? Math.floor(video.duration)
          : 0,
      );
    };

    video.onerror = () => finish(0);

    const timeoutId = window.setTimeout(() => {
      finish(
        Number.isFinite(video.duration) && video.duration > 0
          ? Math.floor(video.duration)
          : 0,
      );
    }, 2000);

    video.load();
  });
};

export const getAssignmentTiming = async (response, videoRefs) => {
  const durationSeconds = await getVideoDurationSeconds(response, videoRefs);

  return {
    total_time: formatTotalTime(durationSeconds),
    avg_time: formatVideoDuration(durationSeconds),
  };
};

export const parseResponsePayload = (response) => {
  try {
    if (!response?.response) return null;

    if (typeof response.response === "string") {
      return JSON.parse(response.response);
    }

    if (typeof response.response === "object") {
      return response.response;
    }

    return null;
  } catch {
    return null;
  }
};

export const getVideoUrlFromResponse = (response) => {
  const videoData = parseResponsePayload(response);
  if (!videoData?.responses) return "";

  const entries = Object.values(videoData.responses);

  for (const entry of entries) {
    if (entry?.type === "record-video" && entry?.value) {
      const url = pickMediaUrl(entry.value);
      if (url) return url;
    }
  }

  for (const entry of entries) {
    const url = pickMediaUrl(entry?.value);
    if (url) return url;
  }

  return "";
};

export const withVideoCacheBuster = (url, id) => {
  const normalizedUrl = pickMediaUrl(url);
  if (!normalizedUrl) return null;

  return `${normalizedUrl}${normalizedUrl.includes("?") ? "&" : "?"}t=${id}`;
};

const normalizeFilterText = (value) =>
  String(value ?? "").trim().toLowerCase();

const getRecordCampaignValues = (campaign) => {
  if (campaign == null || campaign === "") return [];

  if (typeof campaign === "string") {
    return [normalizeFilterText(campaign)];
  }

  return [campaign._id, campaign.title, campaign.name]
    .filter(Boolean)
    .map(normalizeFilterText);
};

const matchesSelectedValue = (selectedValues, recordValues) => {
  if (!selectedValues.length || !recordValues.length) return false;

  return selectedValues.some((selected) => recordValues.includes(selected));
};

export const matchesVideoStatus = (record, status) => {
  if (!status) return true;

  const isAssigned = Boolean(record?.assigned_videos);

  if (status === "assigned") return isAssigned;
  if (status === "unassigned") return !isAssigned;

  return true;
};

export const matchesVideoFilters = (record, filters, lookups = {}) => {
  if (!matchesVideoStatus(record, filters?.status)) return false;

  if (filters?.organization) {
    const selectedOrgValues = [filters.organization, lookups.organizationName]
      .filter(Boolean)
      .map(normalizeFilterText);

    const recordOrgValues = [
      record?.form?.organization?._id,
      record?.form?.organization?.name,
    ]
      .filter(Boolean)
      .map(normalizeFilterText);

    if (!matchesSelectedValue(selectedOrgValues, recordOrgValues)) {
      return false;
    }
  }

  if (filters?.campaign) {
    const selectedCampaignValues = [filters.campaign, lookups.campaignTitle]
      .filter(Boolean)
      .map(normalizeFilterText);
    const recordCampaignValues = getRecordCampaignValues(record?.form?.campaign);

    if (!matchesSelectedValue(selectedCampaignValues, recordCampaignValues)) {
      return false;
    }
  }

  if (filters?.form) {
    const selectedFormValues = [filters.form, lookups.formTitle]
      .filter(Boolean)
      .map(normalizeFilterText);
    const recordFormValues = [record?.form?._id, record?.form?.title]
      .filter(Boolean)
      .map(normalizeFilterText);

    if (!matchesSelectedValue(selectedFormValues, recordFormValues)) {
      return false;
    }
  }

  if (filters?.dateRange?.[0]) {
    const recordDate = new Date(record?.date_created);
    if (Number.isNaN(recordDate.getTime())) return false;

    const start = new Date(filters.dateRange[0].valueOf());
    start.setHours(0, 0, 0, 0);

    const end = new Date((filters.dateRange[1] || filters.dateRange[0]).valueOf());
    end.setHours(23, 59, 59, 999);

    if (recordDate < start || recordDate > end) return false;
  }

  return true;
};

export const sortVideoResponses = (records, sort) => {
  if (!sort || !Array.isArray(records)) return records;

  return [...records].sort((left, right) => {
    const leftTime = new Date(left?.date_created || 0).getTime();
    const rightTime = new Date(right?.date_created || 0).getTime();

    return sort === "asc" ? leftTime - rightTime : rightTime - leftTime;
  });
};
